import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MobileMenuTrigger } from '@/components/Layout';
import { 
  Newspaper, RefreshCw, ExternalLink, Copy, TrendingUp, 
  TrendingDown, Minus, Clock, Search, Zap, AlertCircle,
  ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  MessageSquare, Target, Users, Percent, Banknote, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

// Category colors
const CATEGORY_STYLES = {
  'Policy & Regulation': 'bg-purple-100 text-purple-700 border-purple-200',
  'Rates & Home Loans': 'bg-blue-100 text-blue-700 border-blue-200',
  'Market Trends': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Developer Launches': 'bg-amber-100 text-amber-700 border-amber-200',
  'Funding & Investment': 'bg-pink-100 text-pink-700 border-pink-200',
  'Other': 'bg-slate-100 text-slate-700 border-slate-200'
};

// Impact indicator styles
const IMPACT_STYLES = {
  'positive_for_closures': { 
    label: 'Positive for Closures', 
    icon: TrendingUp, 
    color: 'text-emerald-700', 
    bg: 'bg-emerald-50 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  'monitor_closely': { 
    label: 'Monitor Closely', 
    icon: AlertTriangle, 
    color: 'text-amber-700', 
    bg: 'bg-amber-50 border-amber-200',
    dot: 'bg-amber-500'
  },
  'could_slow_conversions': { 
    label: 'Could Slow Conversions', 
    icon: TrendingDown, 
    color: 'text-red-700', 
    bg: 'bg-red-50 border-red-200',
    dot: 'bg-red-500'
  }
};

// Sentiment styles for pulse
const SENTIMENT_STYLES = {
  'Positive': { icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-300' },
  'Neutral': { icon: Minus, color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-300' },
  'Risk': { icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-300' }
};

// Mood indicator styles
const MOOD_STYLES = {
  buyer_confidence: {
    High: { color: 'text-emerald-600', bg: 'bg-emerald-50' },
    Moderate: { color: 'text-amber-600', bg: 'bg-amber-50' },
    Low: { color: 'text-red-600', bg: 'bg-red-50' }
  },
  negotiation_pressure: {
    Low: { color: 'text-emerald-600', bg: 'bg-emerald-50' },
    Medium: { color: 'text-amber-600', bg: 'bg-amber-50' },
    High: { color: 'text-red-600', bg: 'bg-red-50' }
  },
  loan_sensitivity: {
    Stable: { color: 'text-emerald-600', bg: 'bg-emerald-50' },
    Rising: { color: 'text-amber-600', bg: 'bg-amber-50' },
    Elevated: { color: 'text-red-600', bg: 'bg-red-50' }
  }
};

const MarketIntelligence = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [pulse, setPulse] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [timeRange, setTimeRange] = useState('today');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Admin
  const [refreshing, setRefreshing] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  
  // Quick brief toggles (per article)
  const [expandedBriefs, setExpandedBriefs] = useState({});

  const isAdmin = user?.role === 'super_admin' || user?.role === 'Admin';

  useEffect(() => {
    fetchData();
  }, [timeRange, selectedCategories, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { time_range: timeRange, limit: 20 };
      if (selectedCategories.length > 0) {
        params.categories = selectedCategories.join(',');
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const [newsRes, pulseRes] = await Promise.all([
        api.getMarketNews(params),
        api.getMarketPulse()
      ]);
      
      setArticles(newsRes.data.articles || []);
      setTotal(newsRes.data.total || 0);
      setPulse(pulseRes.data);
      
      // Fetch stats for admin
      if (isAdmin) {
        const statsRes = await api.getMarketNewsStats();
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to load market news:', error);
      toast.error('Failed to load market intelligence');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.refreshMarketNews();
      toast.success(`Refreshed: ${res.data.new_articles} new articles added`);
      fetchData();
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Failed to refresh news');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRebuildSummaries = async () => {
    setRebuilding(true);
    try {
      const res = await api.rebuildMarketSummaries(30);
      toast.success(`Rebuilt intelligence for ${res.data.regenerated} articles`);
      fetchData();
    } catch (error) {
      console.error('Rebuild failed:', error);
      toast.error('Failed to rebuild intelligence');
    } finally {
      setRebuilding(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const copyInsight = (article) => {
    const actionItems = article.action_items?.join('\n• ') || '';
    const text = `${article.title}\n\nWhat Happened:\n${article.what_happened || article.summary}\n\nWhy This Matters:\n${article.why_it_matters}\n\nAction Items:\n• ${actionItems}\n\nTalking Line:\n"${article.talking_line}"`;
    navigator.clipboard.writeText(text);
    toast.success('Intelligence brief copied');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // FIXED: Single active filter - clicking new filter resets others
  const toggleCategory = (cat) => {
    setSelectedCategories(prev => {
      // If already selected, deselect it
      if (prev.includes(cat)) {
        return [];
      }
      // Otherwise, select only this one (single select mode)
      return [cat];
    });
  };

  const toggleQuickBrief = (articleId) => {
    setExpandedBriefs(prev => ({
      ...prev,
      [articleId]: !prev[articleId]
    }));
  };

  const categories = [
    'Policy & Regulation',
    'Rates & Home Loans',
    'Market Trends',
    'Developer Launches',
    'Funding & Investment'
  ];

  const SentimentStyle = pulse?.sentiment ? SENTIMENT_STYLES[pulse.sentiment] : SENTIMENT_STYLES['Neutral'];

  return (
    <div className="min-h-screen bg-slate-50 pb-20 md:pb-0" data-testid="market-intelligence-page">
      {/* Header - Standardized ← Title format (dark variant) */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Back Arrow - Always visible */}
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="page-back-btn"
              >
                <ArrowLeft size={20} className="text-white" strokeWidth={2.5} />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                  <Newspaper size={24} className="hidden md:block" />
                  Market Intelligence
                </h1>
                <p className="text-slate-300 text-xs md:text-sm mt-0.5">Daily Business Briefing</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchData}
                variant="secondary"
                size="sm"
                className="bg-white/10 hover:bg-white/20 text-white border-0 h-9 w-9 p-0 md:h-9 md:w-auto md:px-3"
                data-testid="refresh-view-btn"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </Button>
              
              {isAdmin && (
                <div className="hidden md:flex gap-2">
                  <Button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    variant="secondary"
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                    data-testid="admin-refresh-btn"
                  >
                    {refreshing ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Zap size={14} className="mr-1" />}
                    Fetch News
                  </Button>
                  <Button
                    onClick={handleRebuildSummaries}
                    disabled={rebuilding}
                    variant="secondary"
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white border-0"
                    data-testid="admin-rebuild-btn"
                  >
                    {rebuilding ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
                    Rebuild Intelligence
                  </Button>
                </div>
              )}
              
              {/* Hamburger Menu - Mobile */}
              <MobileMenuTrigger user={user} className="text-white hover:bg-white/10" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Today's Pulse - Enhanced */}
        {pulse && (
          <Card className="p-5 mb-6 bg-white border-l-4 border-l-slate-800" data-testid="todays-pulse">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Today's Pulse</h3>
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Sentiment Badge */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${SentimentStyle.bg} ${SentimentStyle.border}`}>
                    <SentimentStyle.icon size={18} className={SentimentStyle.color} />
                    <span className={`font-semibold ${SentimentStyle.color}`}>{pulse.sentiment}</span>
                  </div>
                  {/* Primary Driver */}
                  <div className="text-slate-600 text-sm">
                    Primary Driver: <span className="font-semibold text-slate-800">{pulse.primary_driver}</span>
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="text-slate-500 shrink-0">
                {pulse.article_count} articles
              </Badge>
            </div>
            
            {/* Sales Mood Snapshot */}
            {pulse.sales_mood && (
              <div className="border-t pt-4 mt-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Sales Mood Snapshot</p>
                <div className="grid grid-cols-3 gap-4">
                  {/* Buyer Confidence */}
                  <div className={`px-3 py-2 rounded-lg ${MOOD_STYLES.buyer_confidence[pulse.sales_mood.buyer_confidence]?.bg || 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Users size={14} className="text-slate-500" />
                      <span className="text-xs text-slate-500">Buyer Confidence</span>
                    </div>
                    <p className={`font-semibold ${MOOD_STYLES.buyer_confidence[pulse.sales_mood.buyer_confidence]?.color || 'text-slate-700'}`}>
                      {pulse.sales_mood.buyer_confidence}
                    </p>
                  </div>
                  
                  {/* Negotiation Pressure */}
                  <div className={`px-3 py-2 rounded-lg ${MOOD_STYLES.negotiation_pressure[pulse.sales_mood.negotiation_pressure]?.bg || 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Percent size={14} className="text-slate-500" />
                      <span className="text-xs text-slate-500">Negotiation Pressure</span>
                    </div>
                    <p className={`font-semibold ${MOOD_STYLES.negotiation_pressure[pulse.sales_mood.negotiation_pressure]?.color || 'text-slate-700'}`}>
                      {pulse.sales_mood.negotiation_pressure}
                    </p>
                  </div>
                  
                  {/* Loan Sensitivity */}
                  <div className={`px-3 py-2 rounded-lg ${MOOD_STYLES.loan_sensitivity[pulse.sales_mood.loan_sensitivity]?.bg || 'bg-slate-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote size={14} className="text-slate-500" />
                      <span className="text-xs text-slate-500">Loan Sensitivity</span>
                    </div>
                    <p className={`font-semibold ${MOOD_STYLES.loan_sensitivity[pulse.sales_mood.loan_sensitivity]?.color || 'text-slate-700'}`}>
                      {pulse.sales_mood.loan_sensitivity}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 bg-white p-4 rounded-lg border">
          {/* Time Range */}
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36" data-testid="time-range-select">
              <Clock size={14} className="mr-1 text-slate-400" />
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>

          {/* Category Pills - Single active filter with smooth transition */}
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`filter-pill px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                  selectedCategories.includes(cat)
                    ? `${CATEGORY_STYLES[cat]} scale-105`
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:scale-102'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px] max-w-sm ml-auto">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search headlines..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
          </form>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {total} article{total !== 1 ? 's' : ''} found
          </p>
          {selectedCategories.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedCategories([])}
              className="text-slate-500"
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Articles */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-slate-400" size={32} />
          </div>
        ) : articles.length === 0 ? (
          <Card className="p-6 text-center" data-testid="no-articles">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-full flex items-center justify-center">
              <Newspaper size={28} className="text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {selectedCategories.length > 0 ? 'No articles in this category' : 'Loading fresh insights...'}
            </h3>
            <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
              {selectedCategories.length > 0 
                ? `No ${selectedCategories[0]} articles today. Try a different filter or check back later.`
                : timeRange === 'today'
                  ? 'New market insights are fetched daily at 6 AM. Try viewing this week\'s articles.'
                  : 'No articles match your current filters.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {timeRange === 'today' && (
                <Button 
                  variant="outline" 
                  onClick={() => setTimeRange('week')}
                  className="text-sm"
                >
                  View This Week
                </Button>
              )}
              {selectedCategories.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedCategories([])}
                  className="text-sm"
                >
                  Clear Filters
                </Button>
              )}
              {isAdmin && (
                <Button onClick={handleRefresh} className="text-sm bg-[#5B3DF5] hover:bg-[#4A2FE0]">
                  <Zap size={14} className="mr-1" />
                  Fetch Now
                </Button>
              )}
            </div>
            {/* Last updated indicator */}
            {pulse?.generated_at && (
              <p className="text-xs text-slate-400 mt-4">
                Last updated: {new Date(pulse.generated_at).toLocaleString('en-IN', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  day: 'numeric',
                  month: 'short'
                })}
              </p>
            )}
          </Card>
        ) : (
          <div className="space-y-4" data-testid="articles-list">
            {articles.map((article) => {
              const impactStyle = IMPACT_STYLES[article.impact_indicator] || IMPACT_STYLES['monitor_closely'];
              const ImpactIcon = impactStyle.icon;
              const isQuickBriefOpen = expandedBriefs[article.article_id];
              
              return (
                <Card 
                  key={article.article_id} 
                  className="overflow-hidden hover:shadow-md transition-shadow"
                  data-testid={`article-${article.article_id}`}
                >
                  {/* Impact Tag Header */}
                  <div className={`px-5 py-2 flex items-center justify-between border-b ${impactStyle.bg}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${impactStyle.dot}`}></span>
                      <ImpactIcon size={14} className={impactStyle.color} />
                      <span className={`text-xs font-semibold uppercase tracking-wide ${impactStyle.color}`}>
                        {impactStyle.label}
                      </span>
                    </div>
                    <Badge className={`${CATEGORY_STYLES[article.category] || CATEGORY_STYLES['Other']} text-xs`}>
                      {article.category}
                    </Badge>
                  </div>
                  
                  <div className="p-5">
                    {/* Headline & Meta */}
                    <a 
                      href={article.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg font-semibold text-slate-800 hover:text-blue-600 transition-colors block mb-2"
                    >
                      {article.title}
                    </a>
                    <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
                      <span className="font-medium">{article.source_name}</span>
                      <span>•</span>
                      <span>{formatDate(article.published_at)}</span>
                    </div>
                    
                    {/* Quick 10-sec Brief Toggle */}
                    <button
                      onClick={() => toggleQuickBrief(article.article_id)}
                      className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mb-4"
                      data-testid={`quick-brief-toggle-${article.article_id}`}
                    >
                      {isQuickBriefOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      Quick 10-sec Brief
                    </button>
                    
                    {/* Quick Brief Content */}
                    {isQuickBriefOpen && article.quick_brief && (
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 text-sm text-slate-700">
                        {article.quick_brief.split('\n').map((line, i) => (
                          <p key={i} className="mb-1">{line}</p>
                        ))}
                      </div>
                    )}
                    
                    {/* Section 1: What Happened */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">What Happened</p>
                      <p className="text-slate-700">{article.what_happened || article.summary || 'Details not available.'}</p>
                    </div>
                    
                    {/* Section 2: Why This Matters */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Why This Matters For Our Business</p>
                      <p className="text-slate-700">{article.why_it_matters || 'Assess impact on your sales pipeline.'}</p>
                    </div>
                    
                    {/* Section 3: What You Should Do - Highlighted */}
                    {article.action_items && article.action_items.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-2">
                          <Target size={14} />
                          What You Should Do
                        </p>
                        <ul className="space-y-2">
                          {article.action_items.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-700">
                              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Section 4: Suggested Talking Line */}
                    {article.talking_line && (
                      <div className="bg-amber-50 border-l-4 border-l-amber-400 p-3 mb-4 rounded-r-lg">
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1 flex items-center gap-2">
                          <MessageSquare size={14} />
                          Suggested Talking Line
                        </p>
                        <p className="text-slate-700 italic">"{article.talking_line}"</p>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(article.source_url, '_blank')}
                      >
                        <ExternalLink size={14} className="mr-1" />
                        View Source
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyInsight(article)}
                      >
                        <Copy size={14} className="mr-1" />
                        Copy Brief
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Admin Stats */}
        {isAdmin && stats && (
          <Card className="mt-6 p-4 bg-slate-800 text-white" data-testid="admin-stats">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-3">Admin Stats</h3>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-2xl font-bold">{stats.total_active}</p>
                <p className="text-xs text-slate-400">Active Articles</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.today_count}</p>
                <p className="text-xs text-slate-400">Today</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total_archived}</p>
                <p className="text-xs text-slate-400">Archived</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.max_records}</p>
                <p className="text-xs text-slate-400">Max Capacity</p>
              </div>
            </div>
            {/* Impact Breakdown */}
            {stats.impact_breakdown && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-xs text-slate-400 mb-2">Impact Distribution</p>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    <span className="text-sm">{stats.impact_breakdown.positive_for_closures || 0} Positive</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="text-sm">{stats.impact_breakdown.monitor_closely || 0} Monitor</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span className="text-sm">{stats.impact_breakdown.could_slow_conversions || 0} Risk</span>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default MarketIntelligence;
